/**
 * The view tabs a route owns, published so the shell's phone menu can show them.
 *
 * A phone has room for one navigation system, not two. The topbar is one, and a
 * route's own tab strip is another, and stacked they took roughly a third of an
 * 800px viewport before either of them had said anything. So below the phone
 * breakpoint everything navigational collapses into a single menu in the topbar —
 * which means the menu has to be able to render tabs that belong to a route the
 * shell knows nothing about.
 *
 * The obvious fix is to lift the workspace's `left` state into App. That would put a
 * decision about one route inside a component that renders eleven of them and should
 * have an opinion about none. So the direction is inverted, the way `EvidenceProvider`
 * already inverts it for the evidence panel: the route keeps its own state and
 * *publishes* a description of the choices, and the shell renders whatever is
 * currently published. A route that publishes nothing gets a menu with no view
 * section, which is the correct menu for a route with no views.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface ViewTab {
  /** Preserved from the tab strip: the same control, wherever it is rendered. */
  readonly testid: string;
  readonly label: string;
  /**
   * Whether this is the view currently on screen — or `null` when the entry is not a
   * view at all.
   *
   * The workspace's `Tables` control sits in the tab strip but does not replace what
   * is in the pane; it opens the artifact rail over it. It travels with the tabs
   * because a reader looking for "where do I go from here" looks in one place, but it
   * must not claim to be a selectable view to a screen reader, and the strip must not
   * render it as one. `null` therefore means "an action that opens an overlay over
   * the current view", and the menu announces it as `aria-haspopup="dialog"`.
   */
  readonly selected: boolean | null;
  choose(): void;
}

interface ViewTabsState {
  readonly tabs: readonly ViewTab[];
  publish(tabs: readonly ViewTab[]): void;
}

const NONE: readonly ViewTab[] = [];

const ViewTabsContext = createContext<ViewTabsState | null>(null);

/** Whether two publications describe the same strip in the same state.
 *
 * Only the fields the menu paints. The `choose` closures differ on every render of
 * the publishing route and comparing them would make this always false, which is
 * exactly the case this exists to absorb: a caller that forgets to memoise its array
 * would otherwise publish a new identity on every render, and each publication would
 * re-render the caller. Bailing out here costs one shallow walk of six entries and
 * turns that mistake into a wasted comparison rather than a spin. */
function same(a: readonly ViewTab[], b: readonly ViewTab[]): boolean {
  return (
    a.length === b.length &&
    a.every((tab, index) => {
      const other = b[index];
      return (
        other !== undefined &&
        tab.testid === other.testid &&
        tab.label === other.label &&
        tab.selected === other.selected
      );
    })
  );
}

export function ViewTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<readonly ViewTab[]>(NONE);
  const publish = useCallback((next: readonly ViewTab[]) => {
    setTabs((previous) => (same(previous, next) ? previous : next));
  }, []);
  const value = useMemo(() => ({ tabs, publish }), [tabs, publish]);
  return <ViewTabsContext.Provider value={value}>{children}</ViewTabsContext.Provider>;
}

/** What is published right now. For the shell's menu. */
export function useViewTabs(): readonly ViewTab[] {
  return useContext(ViewTabsContext)?.tabs ?? NONE;
}

/**
 * Publish this route's tabs for as long as it is mounted, and take them away when it
 * is not.
 *
 * `tabs` should be memoised by the caller — not for correctness, which `same` above
 * guarantees either way, but because an unmemoised array runs a comparison on every
 * render for nothing.
 */
export function usePublishViewTabs(tabs: readonly ViewTab[]): void {
  const context = useContext(ViewTabsContext);
  const publish = context?.publish;
  useEffect(() => {
    if (!publish) return;
    publish(tabs);
    return () => publish(NONE);
  }, [publish, tabs]);
}
